// /src/views/AffiliateDashboard/components/PayoutRequest/index.js
import { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { collection, addDoc, query, where, getDocs, doc, updateDoc } from "firebase/firestore";
import { db } from "../../../../firebaseConfig";

function PayoutRequest({
  availableAmount,
  stats,
  userId,
  minimumPayout = 10,
  isAdmin,
  paypalEmail,
  setPaypalEmail,
}) {
  const [error, setError] = useState(null);
  const [emailInput, setEmailInput] = useState(paypalEmail || "");
  const [emailError, setEmailError] = useState(null);
  const [emailSuccess, setEmailSuccess] = useState(null);

  // === UI HELPERS ===
const getCurrentMonthBreakdown = () => {
  const tiers = stats?.activeMonthlyReferralsByTier || stats?.referralsByTier;
  const parts = [];
  if (tiers?.tier1) parts.push(`${tiers.tier1} @ 20%`);
  if (tiers?.tier2) parts.push(`${tiers.tier2} @ 30%`);
  if (tiers?.tier3) parts.push(`${tiers.tier3} @ 40%`);
  return parts.join(" | ") || "—";
};

  const getNextPayoutDate = () => {
    const today = new Date();
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    return nextMonth.toLocaleDateString();
  };

  const handleSaveEmail = async () => {
    if (!emailInput || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput)) {
      setEmailError("Please enter a valid email address");
      setEmailSuccess(null);
      return;
    }

    try {
      await updateDoc(doc(db, "Users", userId), { paypalEmail: emailInput });
      setPaypalEmail(emailInput);
      setEmailSuccess("PayPal email saved successfully");
      setEmailError(null);
      console.log("PayPal email saved:", emailInput);
    } catch (err) {
      console.error("Error saving PayPal email:", err);
      setEmailError("Failed to save PayPal email");
      setEmailSuccess(null);
    }
  };

  // === DATE + NUMBER HELPERS ===
  const parseSubDate = (createdAt) => {
    if (!createdAt) return null;
    if (createdAt?.seconds) return new Date(createdAt.seconds * 1000);
    if (typeof createdAt === "object" && typeof createdAt.toDate === "function") return createdAt.toDate();
    const d = new Date(createdAt);
    return isNaN(d.getTime()) ? null : d;
  };

  const toNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const startOfMonth = (year, monthIndex0) => new Date(year, monthIndex0, 1, 0, 0, 0, 0);
  const endOfMonth = (year, monthIndex0) => new Date(year, monthIndex0 + 1, 0, 23, 59, 59, 999);

  // Given a payout month (year, monthIndex0), return the earnings period (previous calendar month).
  // Example: payout month = Jan 2026 (0) => earnings period = Dec 2025
  const getEarningsPeriodForPayoutMonth = (year, monthIndex0) => {
    let eMonth = monthIndex0 - 1;
    let eYear = year;
    if (eMonth < 0) {
      eMonth = 11;
      eYear -= 1;
    }
    return {
      earningsYear: eYear,
      earningsMonth: eMonth,
      earningsStart: startOfMonth(eYear, eMonth),
      earningsEnd: endOfMonth(eYear, eMonth),
    };
  };

  // Subscription validity filter (same style as your existing logic)
  const isValidForCommission = (subData) => {
    if (!subData) return false;
    if (subData.gifted === true) return false;
    if (subData.discountPercentage === 100) return false;
    if (subData.amount === 0) return false;
    return true;
  };

  // === TIERS (based on referral count ordering) ===
  // NOTE: We only decide tier order here. We DO NOT do month math here.
  const calculateTierIds = (subscriptions) => {
    const validSubs = subscriptions.filter((s) => isValidForCommission(s.data()));

    const sortedSubs = validSubs
      .slice()
      .sort((a, b) => {
        const dateA = parseSubDate(a.data().createdAt) || new Date(0);
        const dateB = parseSubDate(b.data().createdAt) || new Date(0);
        return dateA - dateB;
      });

    const tier1Subs = sortedSubs.slice(0, 50);
    const tier2Subs = sortedSubs.slice(50, 100);
    const tier3Subs = sortedSubs.slice(100);

    return {
      tier1Ids: tier1Subs.map((s) => s.id),
      tier2Ids: tier2Subs.map((s) => s.id),
      tier3Ids: tier3Subs.map((s) => s.id),
      referralsByTier: {
        tier1: tier1Subs.length,
        tier2: tier2Subs.length,
        tier3: tier3Subs.length,
      },
      currentTier: sortedSubs.length <= 50 ? 1 : sortedSubs.length <= 100 ? 2 : 3,
    };
  };

  const getCommissionRateForSub = (subId, tierIds) => {
    if (tierIds.tier1Ids.includes(subId)) return 0.2;
    if (tierIds.tier2Ids.includes(subId)) return 0.3;
    return 0.4;
  };

  // === MONTHLY PAYOUT AMOUNT (paid in payoutMonth for earnings in previous month) ===
  // Rules you requested:
  // - Monthly plans: commission earned for the earnings month (paid following month)
  // - Annual plans: one-time commission, counted ONLY if createdAt is within earnings month (paid following month)
  const calculatePayoutForMonth = ({ subsUpToEarningsEnd, tierIds, earningsStart, earningsEnd }) => {
    let monthTotal = 0;

    subsUpToEarningsEnd.forEach((subSnap) => {
      const subData = subSnap.data();
      if (!isValidForCommission(subData)) return;

      const created = parseSubDate(subData.createdAt);
      if (!created) return;

      const amountPaid = toNumber(subData.planDetails?.cost);
      const rate = getCommissionRateForSub(subSnap.id, tierIds);
      const commission = amountPaid * rate;

      const isAnnual = subData.plan === "annual_plan";
      const isMonthly = subData.plan === "monthly_plan" || subData.type === "monthly" || subData.type === "month";

      if (isAnnual) {
        // One-time, credited in earnings month only
        const inEarningsMonth = created >= earningsStart && created <= earningsEnd;
        if (inEarningsMonth) monthTotal += commission;
        return;
      }

      // Monthly recurring: pay if the sub is active (as of now / as of end of earnings period).
      // We can't perfectly time-travel without status history, but this matches your current data model.
      if (isMonthly) {
        if (subData.status === "active") monthTotal += commission;
        return;
      }

      // Fallback: if it isn't clearly annual/monthly, treat it like monthly if active.
      if (subData.status === "active") monthTotal += commission;
    });

    return monthTotal;
  };

  // === AUTO-GENERATE MONTHLY PAYOUT REQUESTS (MISSING MONTHS) ===
  useEffect(() => {
    const createMissingPayouts = async () => {
      try {
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth(); // 0–11

        // We generate for the most recent payout month that should exist:
        // Usually you pay "after month closes", so the payout month is the CURRENT month.
        // Example: it's Feb now -> you are paying Jan earnings in Feb -> payout month = Feb.
        let payoutMonth = currentMonth;
        let payoutYear = currentYear;

        // Fetch affiliate codes once
        const codeDocs = await getDocs(
          query(collection(db, "discountCodes"), where("affiliateId", "==", userId))
        );
        const codeValues = codeDocs.docs.map((d) => d.data().code).filter(Boolean);

        if (codeValues.length === 0) {
          console.log("No affiliate codes found, cannot generate payouts.");
          return;
        }

        // Fetch existing payout docs once
        const allPayoutDocsSnap = await getDocs(
          query(collection(db, "payoutRequests"), where("affiliateId", "==", userId))
        );

        const existingPayouts = allPayoutDocsSnap.docs.map((p) => ({
          month: p.data().month,
          year: p.data().year,
        }));

        // Fetch ALL subscriptions once
        const allSubsSnapshot = await getDocs(collection(db, "subscriptions"));
        const allSubs = allSubsSnapshot.docs;

        // Loop backwards creating missing payout months until we hit an existing payout month
        // (prevents infinite backfill).
        while (true) {
          // stop if already exists
          if (existingPayouts.some((p) => p.month === payoutMonth && p.year === payoutYear)) {
            console.log(`Stopping generation: payout already exists for ${payoutMonth + 1}/${payoutYear}`);
            break;
          }

          const payoutStart = startOfMonth(payoutYear, payoutMonth);
          const payoutEnd = endOfMonth(payoutYear, payoutMonth);

          // Earnings period is previous calendar month (YOUR RULE: Dec signup paid in Jan)
          const { earningsStart, earningsEnd } = getEarningsPeriodForPayoutMonth(payoutYear, payoutMonth);

          // Subs attributed to this affiliate, created on/before earningsEnd
          const subsUpToEarningsEnd = allSubs
            .filter((docSnap) => {
              const data = docSnap.data();
              const code = data.planDetails?.discountApplied?.code;
              const giftCode = data.giftCode;
              return codeValues.includes(code) || codeValues.includes(giftCode);
            })
            .filter((docSnap) => {
              const created = parseSubDate(docSnap.data().createdAt);
              return created && created <= earningsEnd;
            });

          // Determine tiers based on all referred subs up to earningsEnd
          const tierIds = calculateTierIds(subsUpToEarningsEnd);

          // Calculate the payout amount for THIS payout month (based on earnings month)
          const monthAmount = Math.max(
            0,
            calculatePayoutForMonth({
              subsUpToEarningsEnd,
              tierIds,
              earningsStart,
              earningsEnd,
            })
          );

          // If nothing earned in that earnings period, stop backfilling further.
          if (monthAmount <= 0) {
            console.log(`Stopping generation: $0 earnings for payout ${payoutMonth + 1}/${payoutYear}`);
            break;
          }

          await addDoc(collection(db, "payoutRequests"), {
            affiliateId: userId,
            amount: monthAmount,
            status: "pending",

            // payout month (THIS is the month they are PAID)
            month: payoutMonth,
            year: payoutYear,

            // for clarity in admin: what month produced these earnings
            earningsPeriodStart: earningsStart,
            earningsPeriodEnd: earningsEnd,

            // optional: payout month window
            payoutPeriodStart: payoutStart,
            payoutPeriodEnd: payoutEnd,

            createdAt: new Date(),
            requestedAt: payoutStart, // table date stays consistent

            paypalEmail: paypalEmail || null,

            breakdown: {
              tier1Count: tierIds.referralsByTier.tier1,
              tier2Count: tierIds.referralsByTier.tier2,
              tier3Count: tierIds.referralsByTier.tier3,
              totalReferrals: subsUpToEarningsEnd.length,
            },
          });

          console.log(
            `Generated payout for ${payoutMonth + 1}/${payoutYear} (earnings from previous month): $${monthAmount.toFixed(2)}`
          );

          // move back 1 payout month
          payoutMonth--;
          if (payoutMonth < 0) {
            payoutMonth = 11;
            payoutYear--;
          }
        }
      } catch (err) {
        console.error("Error generating payouts:", err);
        setError("Failed to generate payouts");
      }
    };

    createMissingPayouts();
  }, [userId, paypalEmail, minimumPayout]);

  // === RENDER ===
  return (
    <Card>
      <MDBox p={3}>
        <MDTypography variant="h6">Monthly Payout Info</MDTypography>

        {error && (
          <MDTypography color="error" variant="caption" mt={1}>
            {error}
          </MDTypography>
        )}

        <MDBox mt={2}>
          <MDTypography variant="body2">Next payout date: {getNextPayoutDate()}</MDTypography>



          <MDTypography variant="h4" mt={1}>
  Est. monthly recurring commission: $
  {Number(stats?.activeMonthlyRecurringCommissionTotal || 0).toFixed(2)}
</MDTypography>

          <MDTypography variant="caption" color="text">
            Monthly recurring estimate from {stats?.activeMonthlyReferrals || 0} active monthly referrals
          </MDTypography>

          <MDTypography variant="caption" display="block" color="text">
            {getCurrentMonthBreakdown()}
          </MDTypography>

          <MDTypography variant="caption" color="text" mt={2}>
            Payouts are generated monthly. Annual signups pay once (credited the month after signup).
          </MDTypography>

          {!isAdmin && (
            <MDBox mt={2}>
              <MDTypography variant="body2" mb={1}>
                PayPal Email for Payouts
              </MDTypography>

              {paypalEmail && (
                <MDTypography variant="body2" mb={1}>
                  Current PayPal Email: {paypalEmail}
                </MDTypography>
              )}

              <MDBox display="flex" alignItems="center" gap={2}>
                <TextField
                  label="PayPal Email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  error={!!emailError}
                  helperText={emailError}
                  size="small"
                  sx={{ maxWidth: "300px" }}
                />
                <MDButton color="primary" size="small" onClick={handleSaveEmail}>
                  Save Email
                </MDButton>
              </MDBox>

              {emailSuccess && (
                <MDTypography color="success" variant="caption" mt={1}>
                  {emailSuccess}
                </MDTypography>
              )}
            </MDBox>
          )}
        </MDBox>
      </MDBox>
    </Card>
  );
}

export default PayoutRequest;